import _ from "lodash";
/** external */
import React from "react";
import PropTypes from "prop-types";
import { colors, fontStyles } from "@toddle-edu/ds-theme";
/** internal */
import { FieldComponent } from "UnitPlans/components/IBcomponents";
import { useI18n } from "Hooks";
import {
  MODE_STUDENT_ATTEMPT,
  MODE_STUDENT_EVALUATE,
  MODE_STUDENT_VIEW,
  MODE_TEACHER_EVALUATE,
  MODE_TEACHER_VIEW,
  MODE_TEACHER_CREATE,
  MODE_TEACHER_PREVIEW,
  WORKSHEET_MODES,
  FIELD_UID_RADIO,
  FIELD_TYPE_RICHTEXT_PORTED,
  FIELD_UID_BLANKS,
  FIELD_UID_GRAPH_INTERACTION_SETTING,
  FIELD_UID_GRAPH_ALGEBRA_INPUT_SETTING,
  FIELD_UID_WORKBOOK_MODIFICATION_SETTING,
  FIELD_UID_WORKBOOK_INTERACTION_SETTING,
  FIELD_UID_FIND_HOTSPOT_VISIBLE_SETTING,
  FIELD_UID_FIND_HOTSPOT_ALLOW_MULTIPLE_SETTING,
  FIELD_UID_MATCHING_RESPONSE,
  DEFAULT_MEDIA_SUBMISSION_METHOD,
  FIELD_UID_SUBMISSION_METHOD,
  RICHTEXT_VIEW_STYLES,
  FIELD_UID_ALLOW_MULTIPLE_SELECTION_SETTING,
  AI_GEN_IDENTIFIER,
  FIELD_UID_DURATION,
} from "Worksheet/modules/WorksheetConstants";
import classNames from "classnames";
import {
  validateCustomQuestionField,
  isStudentEndMode,
} from "Worksheet/modules/WorksheetUtils";
/** relative */
import NoResponseComponent from "../NoResponseComponent";
import Accordion from "./Accordion";
import RadioButtonPicker from "./RadioButtonPicker";
import ToggleSwitchList from "./ToggleSwitchList";
import FillInTheBlanks from "./FillInTheBlanks";
import classes from "./QuestionFieldComponent.scss";
import MultiChoiceCheckList from "./MultiChoiceCheckList";
import RadioButtonList from "./RadioButtonList";
import GraphQuestion from "./GraphQuestion";
import DrawingField from "./DrawingField";
import DragAndDropField from "./DragAndDropField";
import FindHotspotField from "./FindHotspotField";
import MatchingField from "./MatchingField";
import LabellingField from "./LabellingField";
import TableQuestionWrapper from "./TableQuestionWrapper";
import TextViewWrapper from "Worksheet/components/TextViewWrapper";
import WorksheetTextView from "UIComponents/WorksheetTextView";
import DurationPicker from "./DurationPicker";
import AudioResponseField from "./AudioResponseField";
import VideoResponseField from "./VideoResponseField";
import MediaQuestionSubmissionMethod from "./MediaQuestionSubmissionMethod";
import {
  getIsWorksheetDocEditorEnabled,
  getIsStudentCanCopyPasteInWorksheet,
} from "Worksheet/modules/WorksheetFeatureFlags";

const QuestionFieldComponent = props => {
  const {
    hideField,
    type,
    label,
    labelComponent,
    placeholder,
    fieldId,
    parentId,
    parentType,
    fieldValue,
    updateInputField,
    options,
    fieldTypeConfig,
    isRequired,
    fieldUid,
    mode,
    fieldMode,
    showNoResponseText,
    customTextStyle,
    componentStyles,
    questionFields,
    disableAccordionBehaviour,
    defaultOptions,
    customRef,
    isMediaRestricted,
    showDetails,
    onFocusInputField,
    isFromLibrary,
    portalType,
    questionIndex,
    isSubQuestion,
    resourceId,
    questionId,
    subQuestionParentId,
    submissionId,
    questionSettings,
    submissionFields,
    assessmentToolsEvaluationDetails,
    isActiveQuestion,
    learningGoalsMapping,
    getLearningGoalsTagCountTooltip,
    questionTitleText,
    isSubmitted,
    isStudentWorkbookForceViewMode,
    sanitizationType,
    isFromInternalTool,
    questionTypeUid,
    labelConfig,
    isQueriesV2Enabled,
    hideBorder,
    isLockScreenEnabled,
  } = props;

  const isStudentRevamp = isStudentEndMode(mode);

  const showDocEditor =
    getIsWorksheetDocEditorEnabled() &&
    (!_.includes(fieldUid, FIELD_UID_BLANKS) || fieldMode !== "view");

  const { t } = useI18n(["common", "worksheet"]);

  const styles = {
    baseStudentRevampStyle: {
      fontWeight: "500",
      lineHeight: "24px",
      fontSize: "16px",
    },
    baseStudentAnswerReviewStyle: {
      display: "flex",
      fontWeight: "400",
    },
    borderStyles: {
      border: `1px solid ${colors.borderSubtle}`,
      borderRadius: "8px",
      padding: "16px",
    },
    textStyle: {
      fontSize: "16px",
      color: colors.textDefault,
      ...fontStyles.medium,
      fontWeight: "400",
    },
    radioButtonStyle: { width: "120px !important" },
    // Use rich text view styles from WorksheetConstants
    ...RICHTEXT_VIEW_STYLES,
  };

  const getRichTextEditorViewStyle = () => {
    // Prioritize customTextStyle when passed from components
    if (customTextStyle) {
      return customTextStyle;
    }

    if (isStudentRevamp) {
      return hideBorder
        ? styles.baseStudentRevampStyle
        : { ...styles.baseStudentRevampStyle, ...styles.borderStyles };
    }

    if (
      _.includes(
        [
          MODE_STUDENT_EVALUATE,
          MODE_STUDENT_VIEW,
          MODE_TEACHER_EVALUATE,
          MODE_TEACHER_VIEW,
        ],
        mode
      )
    ) {
      return hideBorder
        ? styles.baseStudentAnswerReviewStyle
        : { ...styles.baseStudentAnswerReviewStyle, ...styles.borderStyles };
    }

    return styles.richTextViewModeV2;
  };

  const fieldComponentClasses = classNames({
    [classes.richTextViewMode]: type === FIELD_TYPE_RICHTEXT_PORTED,
  });

  const emptyText = t("worksheet:untitled_question");

  const setFieldRef = ref => {
    if (customRef) {
      customRef(fieldUid, ref);
    }
  };

  const customValidation = value =>
    validateCustomQuestionField({ fieldUid, value });

  // * Checking flag and mode to prevent text pasting in student attempt mode.
  const shouldPreventCopyPaste =
    isLockScreenEnabled &&
    mode === MODE_STUDENT_ATTEMPT &&
    getIsStudentCanCopyPasteInWorksheet();

  const renderWorksheetTextView =
    _.includes(
      ["view", "preview", "evaluate", "teacher-preview", "restricted-view"],
      fieldMode
    ) && _.isEqual("RichText_Ported", type);

  const fieldType = renderWorksheetTextView ? "WorksheetTextView" : type;

  const getFieldComponent = () => {
    const isAiGeneratedField = _.includes(fieldId, AI_GEN_IDENTIFIER);
    if (isAiGeneratedField && _.isEqual(type, FIELD_TYPE_RICHTEXT_PORTED)) {
      return (
        <TextViewWrapper
          value={fieldValue}
          customTextStyle={customTextStyle}
          isAiGeneratedField={isAiGeneratedField}
          showDocEditor={showDocEditor}
        />
      );
    }
    switch (fieldType) {
      case "Accordion": {
        return (
          <Accordion
            type={type}
            labelComponent={labelComponent}
            value={fieldValue}
            disableAccordionBehaviour={disableAccordionBehaviour}
            showDetails={isAiGeneratedField || showDetails}
            isAiGeneratedField={isAiGeneratedField}
            showDocEditor={showDocEditor}
          />
        );
      }
      case "RadioButtonPicker": {
        // Get the correct option for
        const correctOption = _.filter(
          questionFields,
          fields => _.get(fields, "uid") === FIELD_UID_RADIO
        );

        const correctAnswer = _.get(correctOption, "[0].value");
        return (
          <RadioButtonPicker
            value={fieldValue}
            mode={fieldMode}
            updateInputField={updateInputField}
            options={options}
            correctAnswer={correctAnswer}
            width={isFromLibrary ? styles.radioButtonStyle.width : null}
            isAiGeneratedField={isAiGeneratedField}
          />
        );
      }
      case "RadioButtonList": {
        return (
          <RadioButtonList
            {...props}
            type={type}
            label={label}
            labelComponent={labelComponent}
            placeholder={_.isEmpty(placeholder) ? null : t(placeholder)}
            parentId={parentId}
            parentType={parentType}
            value={[fieldValue]}
            updateInputField={updateInputField}
            {...options}
            {...fieldTypeConfig}
            isRequired={isRequired}
            fieldId={fieldId}
            fieldKey={fieldUid}
            key={fieldUid}
            mode={fieldMode}
            styles={componentStyles}
            customRef={setFieldRef}
            customValidation={customValidation}
          />
        );
      }
      case "ToggleSwitchList": {
        return (
          <ToggleSwitchList
            value={fieldValue}
            t={t}
            options={options}
            updateInputField={updateInputField}
            labelComponent={labelComponent}
            fieldKey={fieldUid}
          />
        );
      }
      case "FillInTheBlanks": {
        return (
          <FillInTheBlanks
            value={fieldValue}
            mode={fieldMode}
            updateInputField={updateInputField}
            fieldKey={fieldUid}
            isAiGeneratedField={isAiGeneratedField}
            customRef={setFieldRef}
            customValidation={customValidation}
            emptyText={emptyText}
            t={t}
            worksheetMode={mode}
            hideBorder={hideBorder}
          />
        );
      }
      case "MultiChoiceCheckList":
      case "MultiChoiceListView": {
        // Multi selection is allowed by default
        const allowMultiSelect = _.get(
          questionSettings,
          FIELD_UID_ALLOW_MULTIPLE_SELECTION_SETTING,
          true
        );
        return (
          <MultiChoiceCheckList
            value={fieldValue ? fieldValue : []}
            mode={fieldMode}
            updateInputField={updateInputField}
            fieldKey={fieldUid}
            labelComponent={labelComponent}
            customRef={setFieldRef}
            customValidation={customValidation}
            t={t}
            isAiGeneratedField={isAiGeneratedField}
            allowMultiSelect={allowMultiSelect}
            showDocEditor={showDocEditor}
          />
        );
      }
      case "Geogebra": {
        const allowAlgebra = _.get(
          questionSettings,
          FIELD_UID_GRAPH_ALGEBRA_INPUT_SETTING,
          false
        );
        const allowInteraction = _.get(
          questionSettings,
          FIELD_UID_GRAPH_INTERACTION_SETTING,
          false
        );
        return (
          <GraphQuestion
            key={fieldId}
            value={fieldValue}
            mode={fieldMode}
            updateInputField={updateInputField}
            fieldKey={fieldUid}
            labelComponent={labelComponent}
            customRef={setFieldRef}
            customValidation={customValidation}
            allowInteraction={allowInteraction}
            allowAlgebra={allowAlgebra}
            t={t}
            questionIndex={questionIndex}
            isSubQuestion={isSubQuestion}
            subQuestionParentId={subQuestionParentId}
            resourceId={resourceId}
            questionId={questionId}
            worksheetMode={mode}
            isFromInternalTool={isFromInternalTool}
          />
        );
      }
      case "Workbook": {
        const canEditAddedObjects = _.get(
          questionSettings,
          FIELD_UID_WORKBOOK_MODIFICATION_SETTING,
          false
        );
        const questionDetails = {
          label: questionTitleText,
        };
        const allowWorkbookInteraction = _.get(
          questionSettings,
          FIELD_UID_WORKBOOK_INTERACTION_SETTING,
          false
        );
        return (
          <DrawingField
            value={fieldValue}
            updateInputField={updateInputField}
            fieldKey={fieldUid}
            t={t}
            portalType={portalType}
            questionDetails={questionDetails}
            mode={fieldMode}
            labelComponent={labelComponent}
            canEditAddedObjects={canEditAddedObjects}
            questionFields={questionFields}
            parentId={parentId}
            subQuestionParentId={subQuestionParentId}
            resourceId={resourceId}
            isSubQuestion={isSubQuestion}
            questionId={questionId}
            questionIndex={questionIndex}
            submissionId={submissionId}
            fieldId={fieldId}
            submissionFields={submissionFields}
            allowWorkbookInteraction={allowWorkbookInteraction}
            isStudentWorkbookForceViewMode={isStudentWorkbookForceViewMode}
            worksheetMode={mode}
            isFromInternalTool={isFromInternalTool}
            isQueriesV2Enabled={isQueriesV2Enabled}
          />
        );
      }
      case "DragAndDrop": {
        return (
          <DragAndDropField
            t={t}
            mode={fieldMode}
            fieldId={fieldId}
            value={fieldValue}
            fieldKey={fieldUid}
            questionId={questionId}
            resourceId={resourceId}
            customRef={setFieldRef}
            isSubmitted={isSubmitted}
            isSubQuestion={isSubQuestion}
            questionIndex={questionIndex}
            labelComponent={labelComponent}
            updateInputField={updateInputField}
            subQuestionParentId={subQuestionParentId}
            showViewSolutionButton={
              _.includes([MODE_TEACHER_EVALUATE, MODE_TEACHER_VIEW], mode) ||
              _.get(
                assessmentToolsEvaluationDetails,
                "isSharedWithStudent",
                false
              )
            }
            allowInteraction={
              !(mode === MODE_TEACHER_CREATE && !isActiveQuestion)
            }
            worksheetMode={mode}
            isFromInternalTool={isFromInternalTool}
          />
        );
      }
      case "FindHotspot": {
        const allowToShowHotspot = !_.get(
          questionSettings,
          FIELD_UID_FIND_HOTSPOT_VISIBLE_SETTING,
          false
        );
        const allowToSelectMultipleHotspot = _.get(
          questionSettings,
          FIELD_UID_FIND_HOTSPOT_ALLOW_MULTIPLE_SETTING,
          false
        );
        return (
          <FindHotspotField
            t={t}
            mode={fieldMode}
            fieldId={fieldId}
            value={fieldValue}
            fieldKey={fieldUid}
            questionId={questionId}
            resourceId={resourceId}
            customRef={setFieldRef}
            isSubmitted={isSubmitted}
            isSubQuestion={isSubQuestion}
            questionIndex={questionIndex}
            labelComponent={labelComponent}
            updateInputField={updateInputField}
            allowToShowHotspot={allowToShowHotspot}
            subQuestionParentId={subQuestionParentId}
            allowToSelectMultipleHotspot={allowToSelectMultipleHotspot}
            isTeacherStudentViewMode={MODE_TEACHER_PREVIEW === mode}
            showViewSolutionButton={
              _.includes([MODE_TEACHER_EVALUATE, MODE_TEACHER_VIEW], mode) ||
              _.get(
                assessmentToolsEvaluationDetails,
                "isSharedWithStudent",
                false
              )
            }
            allowInteraction={
              !(mode === MODE_TEACHER_CREATE && !isActiveQuestion)
            }
            worksheetMode={mode}
            isFromInternalTool={isFromInternalTool}
          />
        );
      }
      case "MatchingPair":
      case "MatchingResponse": {
        const matchingPairsFromField = _.get(
          _.find(questionFields, { uid: "matchingPairs" }),
          "value.matchingPairs",
          []
        );
        const matchingResponseFromField = _.find(submissionFields, {
          uid: FIELD_UID_MATCHING_RESPONSE,
        });
        const showViewSolutionButton =
          _.includes([MODE_TEACHER_EVALUATE, MODE_TEACHER_VIEW], mode) ||
          _.get(assessmentToolsEvaluationDetails, "isSharedWithStudent", false);

        return (
          <MatchingField
            t={t}
            mode={mode}
            fieldMode={fieldMode}
            updateInputField={updateInputField}
            key={questionId}
            showViewSolutionButton={showViewSolutionButton}
            matchingPairsFromField={matchingPairsFromField}
            matchingResponseFromField={matchingResponseFromField}
            isSubmitted={isSubmitted}
          />
        );
      }
      case "Table": {
        const isSubmitted = _.get(
          assessmentToolsEvaluationDetails,
          "isSubmitted",
          false
        );
        return (
          <TableQuestionWrapper
            mode={fieldMode}
            t={t}
            value={fieldValue}
            updateInputField={updateInputField}
            isSubmitted={isSubmitted}
            key={fieldId}
          />
        );
      }
      case "Labelling": {
        return (
          <LabellingField
            t={t}
            mode={fieldMode}
            fieldId={fieldId}
            value={fieldValue}
            fieldKey={fieldUid}
            questionId={questionId}
            resourceId={resourceId}
            customRef={setFieldRef}
            isSubmitted={isSubmitted}
            isSubQuestion={isSubQuestion}
            questionIndex={questionIndex}
            labelComponent={labelComponent}
            updateInputField={updateInputField}
            subQuestionParentId={subQuestionParentId}
            showViewSolutionButton={
              _.includes([MODE_TEACHER_EVALUATE, MODE_TEACHER_VIEW], mode) ||
              _.get(
                assessmentToolsEvaluationDetails,
                "isSharedWithStudent",
                false
              )
            }
            allowInteraction={
              !(mode === MODE_TEACHER_CREATE && !isActiveQuestion)
            }
            worksheetMode={mode}
            isFromInternalTool={isFromInternalTool}
          />
        );
      }
      case "WorksheetTextView": {
        // Omit mode from props - fieldMode is passed as mode in WorksheetTextView component
        const restProps = _.omit(props, ["mode"]);

        return (
          <WorksheetTextView
            {...restProps}
            value={fieldValue}
            customTextStyle={getRichTextEditorViewStyle()}
            isRtlAlignmentRequired={true}
            sanitizationType={sanitizationType}
            fieldId={fieldId}
            fieldKey={fieldUid}
            key={fieldId}
            mode={fieldMode}
            showDocEditor={showDocEditor}
            emptyText={emptyText}
            textStyle={styles.textStyle}
            styles={componentStyles}
          />
        );
      }
      case "DurationPicker": {
        return (
          <DurationPicker
            value={fieldValue}
            fieldKey={fieldUid}
            key={fieldId}
            t={t}
            updateInputField={updateInputField}
            questionTypeUid={questionTypeUid}
            labelConfig={labelConfig}
          />
        );
      }
      case "AudioResponse": {
        const audioDurationLimit = _.get(
          questionSettings,
          FIELD_UID_DURATION,
          600000
        );
        const submissionMethod =
          _.get(
            questionSettings,
            FIELD_UID_SUBMISSION_METHOD,
            DEFAULT_MEDIA_SUBMISSION_METHOD
          ) || DEFAULT_MEDIA_SUBMISSION_METHOD;

        return (
          <AudioResponseField
            value={fieldValue}
            fieldKey={fieldUid}
            key={fieldId}
            t={t}
            fieldMode={fieldMode}
            mode={mode}
            updateInputField={updateInputField}
            fieldId={fieldId}
            parentType={"QUESTION_SUBMISSION_ATTACHMENT"}
            audioDurationLimit={audioDurationLimit}
            questionId={questionId}
            isSubQuestion={isSubQuestion}
            isFromInternalTool={isFromInternalTool}
            subQuestionParentId={subQuestionParentId}
            submissionMethod={submissionMethod}
            portalType={portalType}
            isQueriesV2Enabled={isQueriesV2Enabled}
          />
        );
      }
      case "VideoResponse": {
        const videoDurationLimit = _.get(
          questionSettings,
          FIELD_UID_DURATION,
          600000
        );
        const submissionMethod =
          _.get(
            questionSettings,
            FIELD_UID_SUBMISSION_METHOD,
            DEFAULT_MEDIA_SUBMISSION_METHOD
          ) || DEFAULT_MEDIA_SUBMISSION_METHOD;

        return (
          <VideoResponseField
            t={t}
            mode={fieldMode}
            fieldId={fieldId}
            value={fieldValue}
            fieldKey={fieldUid}
            parentId={parentId}
            worksheetMode={mode}
            parentType={"QUESTION_SUBMISSION_ATTACHMENT"}
            questionId={questionId}
            resourceId={resourceId}
            isSubQuestion={isSubQuestion}
            questionIndex={questionIndex}
            updateInputField={updateInputField}
            videoDurationLimit={videoDurationLimit}
            isFromInternalTool={isFromInternalTool}
            subQuestionParentId={subQuestionParentId}
            submissionMethod={submissionMethod}
            portalType={portalType}
            isQueriesV2Enabled={isQueriesV2Enabled}
          />
        );
      }
      case "MediaQuestionSubmissionMethod": {
        return (
          <MediaQuestionSubmissionMethod
            key={fieldId}
            value={fieldValue}
            fieldKey={fieldUid}
            updateInputField={updateInputField}
            t={t}
            questionTypeUid={questionTypeUid}
            options={options}
            labelConfig={labelConfig}
          />
        );
      }
      default: {
        return (
          <div className={fieldComponentClasses}>
            <FieldComponent
              {...props}
              type={type}
              label={label}
              labelComponent={labelComponent}
              placeholder={_.isEmpty(placeholder) ? null : t(placeholder)}
              parentId={parentId}
              parentType={parentType}
              value={fieldValue}
              updateInputField={updateInputField}
              {...options}
              {...fieldTypeConfig}
              isRequired={isRequired}
              fieldId={fieldId}
              fieldKey={fieldUid}
              key={fieldId}
              mode={fieldMode}
              customTextStyle={getRichTextEditorViewStyle()}
              defaultOptions={defaultOptions || options}
              styles={componentStyles}
              customRef={customRef}
              isMediaRestricted={isMediaRestricted}
              onFocusInputField={onFocusInputField}
              customValidation={
                fieldUid == FIELD_UID_BLANKS ? customValidation : null
              }
              emptyText={emptyText}
              textStyle={styles.textStyle}
              renderNewEditor={true}
              portalType={portalType}
              enableEditorObserver={true}
              shouldCallOnBlur={false}
              learningGoalsMapping={learningGoalsMapping}
              getLearningGoalsTagCountTooltip={getLearningGoalsTagCountTooltip}
              hideErrorMessage={true}
              isRtlAlignmentRequired={true}
              sanitizationType={sanitizationType}
              showMediaLinkInTheatreMode={true}
              toolbarPosition="bottom"
              showDocEditor={showDocEditor}
              allowCopyPasteInEquationEditor={!shouldPreventCopyPaste}
              {...(shouldPreventCopyPaste && {
                disabledCopyButtonTooltipText: t(
                  "worksheet:copy_paste_disabled_in_worksheet"
                ),
              })}
            />
          </div>
        );
      }
    }
  };

  return (
    <>
      {hideField ? null : getFieldComponent()}
      {showNoResponseText ? <NoResponseComponent t={t} /> : null}
    </>
  );
};

QuestionFieldComponent.propTypes = {
  type: PropTypes.string,
  label: PropTypes.string,
  hideField: PropTypes.bool,
  placeholder: PropTypes.string,
  fieldId: PropTypes.string,
  parentId: PropTypes.string,
  parentType: PropTypes.string,
  fieldValue: PropTypes.any,
  updateInputField: PropTypes.func,
  options: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  fieldTypeConfig: PropTypes.object,
  isRequired: PropTypes.bool,
  fieldUid: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  mode: PropTypes.oneOf(WORKSHEET_MODES),
  fieldMode: PropTypes.string,
  isActiveQuestion: PropTypes.bool,
  showNoResponseText: PropTypes.bool,
  labelComponent: PropTypes.element,
  customTextStyle: PropTypes.object,
  disableAccordionBehaviour: PropTypes.bool,
  componentStyles: PropTypes.object,
  questionFields: PropTypes.array,
  isMediaRestricted: PropTypes.bool,
  showDetails: PropTypes.bool,
  onFocusInputField: PropTypes.func,
  isFromLibrary: PropTypes.bool,
  submissionId: PropTypes.string,
  questionSettings: PropTypes.object,
  submissionFields: PropTypes.array,
  assessmentToolsEvaluationDetails: PropTypes.object,
  questionTitleText: PropTypes.string,
  isSubmitted: PropTypes.bool,
  isStudentWorkbookForceViewMode: PropTypes.bool,
  isRtlAlignmentRequired: PropTypes.bool,
  sanitizationType: PropTypes.string,
  isFromInternalTool: PropTypes.bool,
  questionTypeUid: PropTypes.string,
  labelConfig: PropTypes.object,
  isQueriesV2Enabled: PropTypes.bool,
  hideBorder: PropTypes.bool,
  isLinkDisabled: PropTypes.bool,
};

QuestionFieldComponent.defaultProps = {
  hideField: false,
  isRtlAlignmentRequired: true,
  sanitizationType: "DOCUMENT_EDITOR",
  isFromInternalTool: false,
  questionTypeUid: "",
  labelConfig: {},
  isQueriesV2Enabled: false,
  hideBorder: false,
  isLinkDisabled: null,
};

export default QuestionFieldComponent;
